import { useState, useEffect } from 'react'
import {
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import DateInput from '../../components/DateInput'
import { fmtDate } from '../../utils/format'
import SortableHeader, { useSort } from '../../components/SortableHeader'
import { useToast } from '../../contexts/ToastContext'

const API_BASE_URL = '/api/v1/dev'

interface ShipmentItem {
  id?: number
  ingredient_id: number
  ingredient_name?: string
  quantity: number
  unit_id: number
  unit_name?: string
  quantity_g?: number | null
  batches?: { internal_code: string | null; lot_number: string | null; quantity_g: number }[]
}

interface Shipment {
  id: number
  code: string
  invoice_number: string | null
  shipped_date: string | null
  status: 'draft' | 'validated' | 'cancelled'
  items_count?: number
  items?: ShipmentItem[]
  notes?: string | null
  validated_at?: string | null
  created_at?: string | null
}

interface Product {
  ingredient_id: number
  name: string
  type: string
  unit_id: number | null
  unit_name: string | null
  available_g: number
  available_qty: number | null
}

interface FormItem {
  ingredient_id: string
  quantity: string
}

export default function Shipments() {
  const toast = useToast()
  const [shipments, setShipments] = useState<Shipment[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [showView, setShowView] = useState(false)
  const [selected, setSelected] = useState<Shipment | null>(null)
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)

  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [shippedDate, setShippedDate] = useState(new Date().toLocaleDateString('sv-SE'))
  const [formItems, setFormItems] = useState<FormItem[]>([{ ingredient_id: '', quantity: '' }])

  const { sortField, sortDirection, handleSort, sortData } = useSort<Shipment>('code', 'desc')
  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 15

  const fetchShipments = async () => {
    try {
      setLoading(true)
      const res = await fetch(`${API_BASE_URL}/shipments`, { headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) setShipments(data.data || [])
    } catch {
      toast.error('No se pudieron cargar las expediciones')
    } finally {
      setLoading(false)
    }
  }

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/shipments/available-products`, { headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) setProducts(data.data || [])
    } catch {
      // silencioso
    }
  }

  useEffect(() => { fetchShipments(); fetchProducts() }, [])

  const sorted = sortData(shipments)
  const totalPages = Math.ceil(sorted.length / perPage)
  const paginated = sorted.slice((currentPage - 1) * perPage, currentPage * perPage)

  const getStatusBadge = (status: string) => {
    const cfg: Record<string, { c: string; t: string }> = {
      draft: { c: 'bg-gray-100 text-gray-700', t: 'Borrador' },
      validated: { c: 'bg-green-100 text-green-800', t: 'Validada' },
      cancelled: { c: 'bg-red-100 text-red-700', t: 'Cancelada' },
    }
    const x = cfg[status] || cfg.draft
    return <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${x.c}`}>{x.t}</span>
  }

  const openCreate = () => {
    setInvoiceNumber('')
    setShippedDate(new Date().toLocaleDateString('sv-SE'))
    setFormItems([{ ingredient_id: '', quantity: '' }])
    setShowCreate(true)
    fetchProducts()
  }

  const productOf = (id: string) => products.find(p => p.ingredient_id.toString() === id)

  const save = async () => {
    const items = formItems
      .filter(it => it.ingredient_id && parseFloat(it.quantity) > 0)
      .map(it => {
        const p = productOf(it.ingredient_id)!
        return { ingredient_id: Number(it.ingredient_id), quantity: parseFloat(it.quantity), unit_id: p.unit_id }
      })
    if (!invoiceNumber.trim()) { toast.error('La factura/albarán es obligatoria'); return }
    if (items.length === 0) { toast.error('Añade al menos un producto con cantidad'); return }
    if (items.some(i => !i.unit_id)) { toast.error('Algún producto no tiene unidad por defecto configurada'); return }

    setSaving(true)
    try {
      const res = await fetch(`${API_BASE_URL}/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ invoice_number: invoiceNumber || null, shipped_date: shippedDate, items }),
      })
      const data = await res.json()
      if (res.ok && data.success) {
        toast.success(data.message || 'Expedición creada')
        setShowCreate(false)
        fetchShipments()
      } else {
        toast.error(data.message || 'Error al crear la expedición')
      }
    } catch {
      toast.error('Error de conexión')
    } finally {
      setSaving(false)
    }
  }

  const validate = async (s: Shipment) => {
    if (!confirm(`Validar la expedición ${s.code}? Se descontará del inventario.`)) return
    setBusyId(s.id)
    try {
      const res = await fetch(`${API_BASE_URL}/shipments/${s.id}/validate`, { method: 'POST', headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) { toast.success(data.message || 'Expedición validada'); fetchShipments(); fetchProducts() }
      else toast.error(data.message || 'No se pudo validar')
    } catch {
      toast.error('Error de conexión')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (s: Shipment) => {
    if (!confirm(`Eliminar la expedición ${s.code}?`)) return
    setBusyId(s.id)
    try {
      const res = await fetch(`${API_BASE_URL}/shipments/${s.id}`, { method: 'DELETE', headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) { toast.success(data.message || 'Expedición eliminada'); fetchShipments() }
      else toast.error(data.message || 'No se pudo eliminar')
    } catch {
      toast.error('Error de conexión')
    } finally {
      setBusyId(null)
    }
  }

  const view = async (s: Shipment) => {
    try {
      const res = await fetch(`${API_BASE_URL}/shipments/${s.id}`, { headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) { setSelected(data.data); setShowView(true) }
    } catch {
      toast.error('No se pudo cargar la expedición')
    }
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <TruckIcon className="h-7 w-7 text-primary-600" />
          Expediciones
        </h1>
        <button
          onClick={openCreate}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Nueva Expedición
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : shipments.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          <TruckIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          No hay expediciones registradas.
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Código" field="code" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Factura/Albarán" field="invoice_number" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Fecha" field="shipped_date" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <SortableHeader label="Estado" field="status" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.code}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.invoice_number || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{fmtDate(s.shipped_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.items_count ?? 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(s.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => view(s)} className="text-primary-600 hover:text-primary-900" title="Ver">
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      {s.status === 'draft' && (
                        <>
                          <button onClick={() => validate(s)} disabled={busyId === s.id} className="text-green-600 hover:text-green-900 disabled:opacity-40" title="Validar">
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                          <button onClick={() => remove(s)} disabled={busyId === s.id} className="text-red-600 hover:text-red-900 disabled:opacity-40" title="Eliminar">
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {s.status === 'cancelled' && (
                        <button onClick={() => remove(s)} disabled={busyId === s.id} className="text-red-600 hover:text-red-900 disabled:opacity-40" title="Eliminar">
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <span className="text-sm text-gray-700">Página {currentPage} de {totalPages} ({sorted.length})</span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Anterior</button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Siguiente</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal crear */}
      {showCreate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreate(false)}></div>
            <div className="bg-white rounded-lg shadow-xl transform transition-all max-w-2xl w-full relative">
              <div className="px-6 pt-5 pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Nueva Expedición</h3>
                  <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Factura / Albarán *</label>
                    <input type="text" required value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm text-sm" placeholder="Nº de factura o albarán" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de expedición</label>
                    <DateInput value={shippedDate} onChange={setShippedDate} className="block w-full border-gray-300 rounded-md shadow-sm text-sm" />
                  </div>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-2">Productos a expedir</label>
                <div className="space-y-2">
                  {formItems.map((it, idx) => {
                    const p = productOf(it.ingredient_id)
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <select
                          value={it.ingredient_id}
                          onChange={e => setFormItems(prev => prev.map((x, i) => i === idx ? { ...x, ingredient_id: e.target.value } : x))}
                          className="flex-1 border-gray-300 rounded-md shadow-sm text-sm"
                        >
                          <option value="">Seleccionar producto…</option>
                          {products.map(pr => (
                            <option key={pr.ingredient_id} value={pr.ingredient_id}>
                              {pr.name} (stock: {pr.available_qty ?? '—'} {pr.unit_name})
                            </option>
                          ))}
                        </select>
                        <input
                          type="number" step="0.01" min="0"
                          value={it.quantity}
                          onChange={e => setFormItems(prev => prev.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                          className="w-24 border-gray-300 rounded-md shadow-sm text-sm"
                          placeholder="Cant."
                        />
                        <span className="w-20 text-sm text-gray-500">{p?.unit_name || ''}</span>
                        <button onClick={() => setFormItems(prev => prev.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700" title="Quitar">
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
                <button onClick={() => setFormItems(prev => [...prev, { ingredient_id: '', quantity: '' }])} className="mt-2 inline-flex items-center text-sm text-primary-600 hover:text-primary-800">
                  <PlusIcon className="h-4 w-4 mr-1" /> Añadir producto
                </button>
              </div>

              <div className="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-3">
                <button onClick={save} disabled={saving} className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                  {saving ? 'Guardando…' : 'Crear borrador'}
                </button>
                <button onClick={() => setShowCreate(false)} className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ver */}
      {showView && selected && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowView(false)}></div>
            <div className="bg-white rounded-lg shadow-xl transform transition-all max-w-2xl w-full relative">
              <div className="px-6 pt-5 pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Expedición {selected.code}</h3>
                  <button onClick={() => setShowView(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                  <div><p className="text-gray-500">Factura/Albarán</p><p className="text-gray-900">{selected.invoice_number || '-'}</p></div>
                  <div><p className="text-gray-500">Fecha</p><p className="text-gray-900">{fmtDate(selected.shipped_date)}</p></div>
                  <div><p className="text-gray-500">Estado</p><div className="mt-1">{getStatusBadge(selected.status)}</div></div>
                </div>
                <div className="border rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lotes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selected.items?.map(it => (
                        <tr key={it.id}>
                          <td className="px-4 py-2 text-gray-900">{it.ingredient_name}</td>
                          <td className="px-4 py-2 text-gray-700">{it.quantity} {it.unit_name}{it.quantity_g ? ` (${it.quantity_g} g)` : ''}</td>
                          <td className="px-4 py-2 text-gray-500">
                            {it.batches && it.batches.length > 0
                              ? it.batches.map((b, i) => <div key={i}>{b.internal_code || b.lot_number} · {b.quantity_g} g</div>)
                              : <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-3 flex flex-row-reverse">
                <button onClick={() => setShowView(false)} className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
